import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaSpinner, FaTimes } from 'react-icons/fa';
import {
    TextField,
    Button,
    InputAdornment,
    IconButton,
    Typography,
    Fade,
    Alert,
    AlertTitle,
    Paper,
    Snackbar
} from '@mui/material';
import {
    Email,
    PersonSearch,
    ArrowBack,
    CheckCircle
} from '@mui/icons-material';
import LoadingSpinner from "../utils/LoadingSpinner";
import UserService from '../services/UserService';

const ForgotUsername = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [email, setEmail] = useState('');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorVisible, setErrorVisible] = useState(false);
    const [successVisible, setSuccessVisible] = useState(false);
    const [redirectCountdown, setRedirectCountdown] = useState(3);
    const navigate = useNavigate();
    const redirectTimerRef = useRef(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 3000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (error) {
            setTimeout(() => setErrorVisible(true), 100);
        } else {
            setErrorVisible(false);
        }
    }, [error]);

    useEffect(() => {
        if (success) {
            setTimeout(() => setSuccessVisible(true), 100);
            // Démarrer le compte à rebours pour la redirection
            let countdown = 3;
            setRedirectCountdown(countdown);
            redirectTimerRef.current = setInterval(() => {
                countdown -= 1;
                setRedirectCountdown(countdown);
                if (countdown <= 0) {
                    clearInterval(redirectTimerRef.current);
                    navigate('/login', {
                        state: {
                            message: 'Un rappel de votre identifiant a été envoyé à votre adresse email.',
                            email: email
                        }
                    });
                }
            }, 1000);
        } else {
            if (redirectTimerRef.current) {
                clearInterval(redirectTimerRef.current);
                redirectTimerRef.current = null;
            }
        }

        return () => {
            if (redirectTimerRef.current) {
                clearInterval(redirectTimerRef.current);
                redirectTimerRef.current = null;
            }
        };
    }, [success, navigate, email]);

    const handleRecoverUsername = async (e) => {
        e.preventDefault();

        // Validation de l'email
        if (!email || !email.trim()) {
            setError('Veuillez entrer une adresse email');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Veuillez entrer une adresse email valide');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(false);
        setErrorVisible(false);
        setSuccessVisible(false);

        try {
            await UserService.sendUsernameReminder(email.trim());
            setSuccess(true);
        } catch (error) {
            if (error.response) {
                switch (error.response.status) {
                    case 404:
                        setError('Aucun compte associé à cette adresse email.');
                        break;
                    case 400:
                        setError('Format d\'email invalide.');
                        break;
                    case 429:
                        setError('Trop de tentatives. Veuillez patienter quelques minutes.');
                        break;
                    default:
                        setError(error.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.');
                }
            } else if (error.request) {
                setError('Erreur de connexion au serveur. Vérifiez votre connexion internet.');
            } else {
                setError(error.message || 'Une erreur est survenue. Veuillez réessayer.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGoToLogin = () => {
        if (redirectTimerRef.current) {
            clearInterval(redirectTimerRef.current);
            redirectTimerRef.current = null;
        }
        navigate('/login', {
            state: {
                message: 'Un rappel de votre identifiant a été envoyé à votre adresse email.',
                email: email
            }
        });
    };

    const hideError = () => {
        setErrorVisible(false);
        setTimeout(() => setError(null), 300);
    };

    const hideSuccess = () => {
        setSuccessVisible(false);
        setTimeout(() => setSuccess(false), 300);
        if (redirectTimerRef.current) {
            clearInterval(redirectTimerRef.current);
            redirectTimerRef.current = null;
        }
    };

    return (
        <>
            <LoadingSpinner isVisible={isLoading} loadingText="Chargement..." />

            <div style={{
                backgroundImage: `url('assets/images/banner/bg-register.jpg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    padding: '20px',
                    borderRadius: '10px',
                    width: '100%',
                    maxWidth: '500px',
                    margin: '20px auto',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease'
                }}>
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <Link to="/">
                            <img
                                src='/assets/images/logo/logo.png'
                                alt="Logo"
                                style={{
                                    width: '100px',
                                    height: '100px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '3px solid rgb(23 50 71)'
                                }}
                                onError={(e) => {
                                    e.target.src = '/assets/images/logo/1.png';
                                }}
                            />
                        </Link>
                    </div>

                    <Typography variant="h4" style={{ textAlign: 'center', marginBottom: '10px' }}>
                        Identifiant oublié
                    </Typography>
                    <Typography variant="body2" style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>
                        Entrez votre email pour recevoir un rappel de votre identifiant
                    </Typography>

                    <form onSubmit={handleRecoverUsername} style={{ marginTop: 10 }}>
                        <Fade in timeout={1000}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <TextField
                                    label="Adresse email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading || success}
                                    required
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {error && (
                                    <Alert
                                        severity="error"
                                        style={{
                                            opacity: errorVisible ? 1 : 0,
                                            transition: 'opacity 0.3s ease-in-out'
                                        }}
                                        action={
                                            <IconButton
                                                aria-label="close"
                                                color="inherit"
                                                size="small"
                                                onClick={hideError}
                                            >
                                                <FaTimes />
                                            </IconButton>
                                        }
                                    >
                                        {error}
                                    </Alert>
                                )}

                                {success && (
                                    <Fade in={successVisible} timeout={500}>
                                        <Paper elevation={0} style={{ padding: '15px', backgroundColor: '#e8f5e9' }}>
                                            <Alert
                                                severity="success"
                                                icon={<CheckCircle />}
                                                style={{ marginBottom: '15px' }}
                                                action={
                                                    <IconButton
                                                        aria-label="close"
                                                        color="inherit"
                                                        size="small"
                                                        onClick={hideSuccess}
                                                    >
                                                        <FaTimes />
                                                    </IconButton>
                                                }
                                            >
                                                <AlertTitle>Email envoyé avec succès !</AlertTitle>
                                                Un rappel de votre identifiant a été envoyé à votre adresse email.
                                            </Alert>

                                            <div style={{
                                                backgroundColor: 'white',
                                                padding: '15px',
                                                borderRadius: '8px',
                                                textAlign: 'center',
                                                marginTop: '10px'
                                            }}>
                                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                                    Redirection vers la page de connexion dans {redirectCountdown} secondes...
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    size="medium"
                                                    onClick={handleGoToLogin}
                                                    style={{
                                                        backgroundColor: '#1d8d36',
                                                        marginTop: '10px'
                                                    }}
                                                >
                                                    <PersonSearch style={{ fontSize: '16px', marginRight: '5px' }} />
                                                    Aller à la connexion maintenant
                                                </Button>
                                            </div>
                                        </Paper>
                                    </Fade>
                                )}

                                {!success && (
                                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disabled={loading || !email}
                                            style={{
                                                backgroundColor: loading || !email ? 'rgba(29,141,54,0.58)' : '#1d8d36',
                                                minWidth: '200px',
                                                transition: 'background-color 0.3s ease',
                                            }}
                                        >
                                            {loading ? (
                                                <>
                                                    <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '18px' }} />
                                                    <span style={{ marginLeft: '8px' }}>Envoi en cours...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Email style={{ marginRight: '8px' }} />
                                                    Envoyer le rappel
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                )}

                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <Link to="/login" style={{
                                        color: '#1d8d36',
                                        textDecoration: 'none',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px'
                                    }}>
                                        <ArrowBack style={{ fontSize: '16px' }} />
                                        Retour à la connexion
                                    </Link>
                                </div>
                            </div>
                        </Fade>
                    </form>
                </div>

                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        </>
    );
};

export default ForgotUsername;